import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Download, ShieldCheck, Smartphone, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const LUCKY_HOSTS = new Set(["luckyvault.games", "www.luckyvault.games"]);
const HITTING_HOSTS = new Set(["hittingslots.com", "www.hittingslots.com"]);
const RICK_ELITE_HOSTS = new Set(["rickelitespins.com", "www.rickelitespins.com"]);

const APP_BRANDS = {
    namiGames: {
        hosts: new Set(["nami.games", "www.nami.games", "nami-games.zone.multiple.games"]),
        name: "Nami Games", tagline: "A little sparkle. A world of play.",
        description: "Nami Games — games, rewards, wallet access and friendly support.",
        versionUrl: "/download/nami-games-version.json", downloadFallback: "/download/nami-games-1.0.0.apk",
        iconUrl: "/brands/nami-games/icon.png", wordmarkUrl: "/brands/nami-games/wordmark.svg",
        launchTextLogoUrl: "/brands/nami-games/icon.png", manifestUrl: "/brands/nami-games/manifest.json",
        appRegex: /NamiGamesAndroid/i, versionName: "1.0.0", statusBarColor: "#fffae5",
        accent: "#f9c94e", background: "#073b3c", surface: "#0e5351", domain: "nami.games",
    },
    luckyVault: {
        hosts: LUCKY_HOSTS,
        name: "Lucky Vault",
        tagline: "Your vault. Your games.",
        description: "Lucky Vault — your secure home for games, rewards, wallet access and account updates.",
        versionUrl: "/download/lucky-vault-version.json",
        downloadFallback: "/download/lucky-vault-1.0.18.apk",
        iconUrl: "/brands/lucky-vault/lucky-vault-app-icon.png?v=centered-3",
        wordmarkUrl: "/brands/lucky-vault/lucky-vault-wordmark.png",
        launchTextLogoUrl: "/brands/lucky-vault/lucky-vault-text-logo.png?v=launch-2",
        manifestUrl: "/brands/lucky-vault/manifest.json?v=centered-3",
        appRegex: /LuckyVaultAndroid/i,
        versionName: "1.0.18",
        statusBarColor: "#fffae5",
        accent: "#f6c64a",
        background: "#030906",
        surface: "#0a2a1d",
        domain: "luckyvault.games",
    },
    hittingSlots: {
        hosts: HITTING_HOSTS,
        name: "Hitting Slots",
        tagline: "Hit play. Find your favorite.",
        description: "Hitting Slots — your bright, polished home for games, rewards, wallet access and account updates.",
        versionUrl: "/download/hitting-slots-version.json",
        downloadFallback: "/download/hitting-slots-1.0.5.apk",
        iconUrl: "/brands/hitting-slots/hitting-slots-icon.png?v=hs-1",
        wordmarkUrl: "/brands/hitting-slots/hitting-slots-icon.png?v=hs-1",
        launchTextLogoUrl: "/brands/hitting-slots/hitting-slots-wordmark.png?v=hs-1",
        manifestUrl: "/brands/hitting-slots/manifest.json?v=hs-1",
        appRegex: /HittingSlotsAndroid/i,
        versionName: "1.0.5",
        statusBarColor: "#fffae5",
        accent: "#ffd43b",
        background: "#020a18",
        surface: "#06356c",
        domain: "hittingslots.com",
    },
    rickEliteSpins: {
        hosts: RICK_ELITE_HOSTS,
        name: "Rick Elite Spins",
        tagline: "Spins and Win",
        description: "Rick Elite Spins — a polished home for games, rewards, wallet access and account updates.",
        versionUrl: "/download/rick-elite-spins-version.json",
        downloadFallback: "/download/rick-elite-spins-1.0.1.apk",
        iconUrl: "/brands/rick-elite-spins/rick-elite-spins-512.png?v=res-2",
        wordmarkUrl: "/brands/rick-elite-spins/rick-elite-spins-text-logo.png?v=res-2",
        launchTextLogoUrl: "/brands/rick-elite-spins/rick-elite-spins-text-logo.png?v=res-2",
        manifestUrl: "/brands/rick-elite-spins/manifest.json?v=res-2",
        appRegex: /RickEliteSpinsAndroid/i,
        versionName: "1.0.1",
        statusBarColor: "#fffae5",
        accent: "#f6c64a",
        background: "#000000",
        surface: "#2f2414",
        domain: "rickelitespins.com",
    },
};

function currentAppBrand() {
    if (typeof window === "undefined") return null;
    const hostname = window.location.hostname.toLowerCase();
    return Object.values(APP_BRANDS).find((brand) => brand.hosts.has(hostname)) || null;
}

function currentNativeBridge() {
    if (typeof window === "undefined") return null;
    return window.NamiGamesAndroid || window.RickEliteSpinsAndroid || window.HittingSlotsAndroid || window.LuckyVaultAndroid || null;
}

export function isLuckyVaultHost() {
    if (typeof window === "undefined") return false;
    return LUCKY_HOSTS.has(window.location.hostname.toLowerCase());
}

export function isHittingSlotsHost() {
    if (typeof window === "undefined") return false;
    return HITTING_HOSTS.has(window.location.hostname.toLowerCase());
}

export function isRickEliteSpinsHost() {
    if (typeof window === "undefined") return false;
    return RICK_ELITE_HOSTS.has(window.location.hostname.toLowerCase());
}

function upsertMeta(selector, attributes) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

export function LuckyVaultMetadata() {
    const location = useLocation();

    useEffect(() => {
        const appBrand = currentAppBrand();
        if (!appBrand) return undefined;
        const description = appBrand.description;
        const title = location.pathname === "/download" || location.pathname === "/get-app"
            ? `Download ${appBrand.name} for Android`
            : location.pathname === "/signup"
            ? `Create account · ${appBrand.name}`
            : location.pathname === "/login"
            ? `Sign in · ${appBrand.name}`
            : `${appBrand.name} · ${appBrand.tagline}`;
        const enforceTitle = () => {
            if (document.title !== title) document.title = title;
        };
        const isHittingSlots = appBrand === APP_BRANDS.hittingSlots;
        document.documentElement.classList.toggle("hitting-slots-no-scrollbar", isHittingSlots);
        document.body.classList.toggle("hitting-slots-no-scrollbar", isHittingSlots);
        document.body.classList.toggle("nami-games-brand", appBrand === APP_BRANDS.namiGames);
        enforceTitle();

        const observer = new MutationObserver(enforceTitle);
        observer.observe(document.querySelector("title"), { childList: true, characterData: true, subtree: true });

        document.querySelector('meta[name="description"]')?.setAttribute("content", description);
        if (window.__APP_STARTUP?.finished) document.querySelector('meta[name="theme-color"]')?.setAttribute("content", appBrand.statusBarColor);
        document.getElementById("app-favicon")?.setAttribute("href", appBrand.iconUrl);
        document.getElementById("app-manifest")?.setAttribute("href", appBrand.manifestUrl);
        upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
        upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: `https://${appBrand.domain}${appBrand.wordmarkUrl.split("?")[0]}` });

        return () => observer.disconnect();
    }, [location.pathname]);

    return null;
}

function isAndroid() {
    return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

function PlayStoreMark({ className = "h-5 w-5" }) {
    return (
        <svg className={className} viewBox="0 0 32 32" aria-hidden="true">
            <path fill="#00d6ff" d="M4.7 3.7c-.45.5-.7 1.2-.7 2.05v20.5c0 .85.25 1.55.7 2.05L16.1 16 4.7 3.7Z" />
            <path fill="#ffd54a" d="m20.1 11.7-12.7-7.3c-.85-.5-1.65-.65-2.25-.45L16.1 16l4-4.3Z" />
            <path fill="#ff4b55" d="M5.15 28.05c.6.2 1.4.05 2.25-.45l12.7-7.3-4-4.3L5.15 28.05Z" />
            <path fill="#42df78" d="m27.15 15-7.05-4.05L16.1 16l4 5.05L27.15 17c1.15-.65 1.15-1.35 0-2Z" />
        </svg>
    );
}

function isCurrentBrandApp(appBrand) {
    return !!appBrand && typeof navigator !== "undefined" && appBrand.appRegex.test(navigator.userAgent);
}

function useLuckyVaultRelease() {
    const [release, setRelease] = useState(null);

    useEffect(() => {
        const appBrand = currentAppBrand();
        if (!appBrand) return;
        const controller = new AbortController();
        fetch(appBrand.versionUrl, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) throw new Error("Release manifest unavailable");
                return response.json();
            })
            .then(setRelease)
            .catch(() => {});
        return () => controller.abort();
    }, []);

    return release;
}

// The pre-React owner persists across tenant resolution and route remounts.
// Native and web never render a second launch overlay here.
export function LuckyVaultLaunch({ tenant = null }) {
    useEffect(() => {
        if (tenant) window.__APP_STARTUP?.setIdentity(tenant);
    }, [tenant]);
    return null;
}

export function LuckyVaultInstallPrompt() {
    const navigate = useNavigate();
    const location = useLocation();
    const release = useLuckyVaultRelease();
    const appBrand = currentAppBrand();
    const [dismissed, setDismissed] = useState(false);

    const shouldShow = useMemo(() => {
        if (!appBrand || !isAndroid() || isCurrentBrandApp(appBrand) || !release?.versionName) return false;
        if (location.pathname === "/download" || location.pathname === "/get-app") return false;
        return true;
    }, [appBrand, location.pathname, release]);

    const dismiss = () => {
        setDismissed(true);
    };

    const download = () => {
        navigate("/download");
    };

    return (
        <AnimatePresence>
            {shouldShow && !dismissed && (
                <motion.aside
                    initial={{ opacity: 0, y: 28, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 16, scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 360, damping: 30, delay: 0.6 }}
                    className="fixed bottom-[calc(0.65rem+env(safe-area-inset-bottom))] left-3 right-3 z-[9000] mx-auto max-w-md overflow-hidden rounded-2xl border border-[#f6c64a]/20 bg-[#06140f]/95 px-3 py-2.5 shadow-[0_18px_56px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:left-auto sm:right-5 sm:w-[390px]"
                    style={{ backgroundColor: `${appBrand?.background || "#06140f"}f2` }}
                    role="dialog"
                    aria-modal="false"
                    aria-label={`${appBrand?.name || "Android"} app is available`}
                >
                    <div
                        className="absolute inset-0"
                        style={{ background: `radial-gradient(circle at 0% 0%, ${appBrand?.surface || "#16a368"}99, transparent 52%)` }}
                    />
                    <div className="relative flex items-center gap-2.5 pr-6">
                        <img
                            src={appBrand?.iconUrl}
                            alt=""
                            className="h-11 w-11 shrink-0 rounded-xl shadow-lg ring-1 ring-white/15"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-extrabold text-white">Get the {appBrand?.name} app</div>
                            <div className="mt-0.5 text-[11px] text-white/55">Faster, smoother Android access</div>
                        </div>
                        <button
                            type="button"
                            onClick={download}
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[#f6c64a] px-3 py-2 text-[11px] font-black text-[#07110d] shadow-[0_8px_24px_rgba(246,198,74,0.2)] transition hover:bg-[#ffe184]"
                        >
                            <PlayStoreMark className="h-4 w-4" />
                            Get app
                        </button>
                        <button
                            type="button"
                            onClick={dismiss}
                            className="absolute -right-1 -top-1 rounded-full p-1 text-white/45 transition hover:bg-white/10 hover:text-white"
                            aria-label="Dismiss app download notification"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </motion.aside>
            )}
        </AnimatePresence>
    );
}

export function LuckyVaultGetAppPage() {
    const navigate = useNavigate();
    const release = useLuckyVaultRelease();
    const appBrand = currentAppBrand() || APP_BRANDS.luckyVault;
    const downloadUrl = release?.downloadUrl || appBrand.downloadFallback;

    return (
        <main className="min-h-[100dvh] overflow-hidden text-white" style={{ background: appBrand.background }}>
            <div
                className="pointer-events-none fixed inset-0"
                style={{ background: `radial-gradient(circle at 12% 15%, ${appBrand.surface}aa, transparent 34%), radial-gradient(circle at 88% 82%, ${appBrand.accent}26, transparent 32%)` }}
            />
            <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-6 sm:px-8 lg:px-10">
                <header className="flex items-center justify-between">
                    {appBrand === APP_BRANDS.hittingSlots ? (
                        <img src={appBrand.iconUrl} alt={appBrand.name} className="h-16 w-16 rounded-[1.35rem] object-contain" />
                    ) : (
                        <div className="flex items-center gap-3">
                            <img src={appBrand.iconUrl} alt="" className="h-12 w-12 rounded-2xl object-contain" />
                            <img src={appBrand.wordmarkUrl} alt={appBrand.name} className="h-11 w-44 object-contain object-left sm:w-56" />
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate("/login")}
                        className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
                    >
                        Sign in
                    </button>
                </header>

                <section className="grid items-center gap-12 pb-12 pt-16 lg:grid-cols-[1.08fr_0.92fr] lg:pt-24">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#f6c64a]/25 bg-[#f6c64a]/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.15em] text-[#f6c64a]">
                            <Smartphone className="h-3.5 w-3.5" /> Official Android app
                        </div>
                        <h1 className="mt-6 max-w-3xl text-5xl font-black leading-[0.98] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
                            Your games,
                            <span className="block bg-gradient-to-r from-[#fff0a7] via-[#f6c64a] to-[#c98a12] bg-clip-text text-transparent">
                                ready when you are.
                            </span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base leading-7 text-white/60 sm:text-lg">
                            Open {appBrand.name} faster, keep your sign-in secure, and receive account and chat alerts in one focused app experience.
                        </p>
                        <div className="mt-8 max-w-xl rounded-3xl border border-[#f6c64a]/25 bg-white/[0.045] p-2 shadow-[0_30px_100px_rgba(0,0,0,0.38)] backdrop-blur">
                            <a
                                href={downloadUrl}
                                className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#efb52e] to-[#ffd96b] px-5 py-4 text-base font-black text-[#06110c] transition hover:brightness-110"
                                download
                            >
                                <Download className="h-5 w-5" /> Download {appBrand.name}
                                <ArrowRight className="h-5 w-5" />
                            </a>
                            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 text-[11px] font-semibold text-white/45">
                                <span>Version {release?.versionName || appBrand.versionName}</span>
                                <span>{release?.sizeLabel || "Android 8.0+"}</span>
                                <span>Official signed APK</span>
                            </div>
                        </div>
                        <p className="mt-3 text-xs text-white/38">
                            Android may ask you to allow installs from this browser. Download only from {appBrand.domain}.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 18, rotate: 1.5 }}
                        animate={{ opacity: 1, y: 0, rotate: 0 }}
                        transition={{ duration: 0.65, ease: "easeOut" }}
                        className="relative mx-auto w-full max-w-md"
                    >
                        <div className="absolute inset-8 rounded-full blur-3xl" style={{ backgroundColor: `${appBrand.surface}88` }} />
                        <div
                            className="relative overflow-hidden rounded-[2.3rem] border border-white/10 p-6 shadow-[0_38px_120px_rgba(0,0,0,0.55)]"
                            style={{ background: `linear-gradient(180deg, ${appBrand.surface}, ${appBrand.background})` }}
                        >
                            {appBrand === APP_BRANDS.hittingSlots ? (
                                <div className="flex flex-col items-center text-center">
                                    <img src={appBrand.iconUrl} alt={appBrand.name} className="h-28 w-28 rounded-[2rem] object-contain ring-1 ring-white/15" />
                                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-white/38">Official Android app</p>
                                </div>
                            ) : (
                                <div className="flex items-center gap-4">
                                    <img src={appBrand.iconUrl} alt={`${appBrand.name} app icon`} className="h-20 w-20 rounded-3xl object-contain ring-1 ring-white/15" />
                                    <div>
                                        <img src={appBrand.wordmarkUrl} alt={appBrand.name} className="max-h-16 w-56 object-contain object-left" />
                                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-white/38">Android app</p>
                                    </div>
                                </div>
                            )}
                            <div className="mt-8 rounded-3xl border border-white/8 bg-white/[0.045] p-5">
                                {["Secure tenant-only sign-in", "Responsive game and wallet access", "Native account and chat alerts"].map((item) => (
                                    <div key={item} className="flex items-center gap-3 border-b border-white/5 py-3 last:border-0">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#14a76c]/15 text-[#5ee0a4]">
                                            <ShieldCheck className="h-4 w-4" />
                                        </span>
                                        <span className="text-sm font-semibold text-white/78">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </section>

                <section className="grid gap-4 sm:grid-cols-3">
                    {[
                        ["01", "Download", "Tap the official download button above."],
                        ["02", "Install", "Allow this source if Android asks, then install."],
                        ["03", "Sign in", `Use your existing ${appBrand.name} account.`],
                    ].map(([number, title, copy]) => (
                        <div key={number} className="rounded-2xl border border-white/8 bg-white/[0.035] p-5">
                            <span className="text-xs font-black tracking-[0.2em] text-[#f6c64a]">{number}</span>
                            <h2 className="mt-4 text-lg font-black">{title}</h2>
                            <p className="mt-2 text-sm leading-6 text-white/48">{copy}</p>
                        </div>
                    ))}
                </section>

                <footer className="mt-12 border-t border-white/8 pt-6 text-center text-xs text-white/35">
                    <span>© 2026 {appBrand.name}. All rights reserved.</span>
                </footer>
            </div>
        </main>
    );
}
